import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { useQR } from '@/hooks/useQRContext';
import { useToast } from '@/components/common/Toast';
import { QRPreview, QRInput, QRCustomizer } from '@/components/qr';
import { Button, Modal, Input } from '@/components/common';

/**
 * Main QR Generator page
 * Provides full QR code generation and customization interface
 */
function GeneratorPage() {
  const { qrConfig, resetConfig, saveDesign, loadFromShareableLink } = useQR();
  const { success, error } = useToast();
  const [searchParams] = useSearchParams();
  
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [designName, setDesignName] = useState('');

  // Load config from URL if present
  useEffect(() => {
    const configParam = searchParams.get('config');
    if (configParam) {
      const loaded = loadFromShareableLink(configParam);
      if (loaded) {
        success('Design loaded from shared link!');
      }
    }
  }, [searchParams, loadFromShareableLink, success]);

  // Handle save design
  const handleSaveDesign = () => {
    if (!qrConfig.data) {
      error('Please enter content for your QR code first');
      return;
    }
    setIsSaveModalOpen(true);
  };

  // Confirm save
  const handleConfirmSave = () => {
    const design = saveDesign(designName || `QR Design ${new Date().toLocaleDateString()}`);
    success(`Design "${design.name}" saved successfully!`);
    setIsSaveModalOpen(false);
    setDesignName('');
  };

  // Handle reset
  const handleReset = () => {
    resetConfig();
    success('Settings reset to default');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">QR Code Generator</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Create and customize your perfect QR code
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={handleReset}
            icon={RotateCcw}
          >
            Reset
          </Button>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Configuration */}
          <div className="space-y-6">
            {/* Input Section */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                1. Enter Content
              </h2>
              <QRInput />
            </div>

            {/* Customization Section */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                2. Customize Design
              </h2>
              <QRCustomizer />
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                3. Preview & Download
              </h2>
              <QRPreview onSave={handleSaveDesign} />
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 p-6 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-2xl border border-primary-100 dark:border-primary-800">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Pro Tips</h3>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>Use high error correction (H) when adding a logo for better scan reliability</li>
            <li>Keep good contrast between the QR code and background colors</li>
            <li>Test your QR code before printing by scanning with your phone</li>
            <li>SVG format is best for print materials, PNG for digital use</li>
            <li>Try gradient colors for eye-catching designs - metallic presets look great!</li>
          </ul>
        </div>
      </div>

      {/* Save Design Modal */}
      <Modal
        isOpen={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        title="Save Design"
        description="Give your design a name to find it easily later"
      >
        <div className="space-y-4">
          <Input
            label="Design Name"
            value={designName}
            onChange={(e) => setDesignName(e.target.value)}
            placeholder="My Awesome QR"
            autoFocus
          />
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setIsSaveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSave}
            >
              Save Design
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default GeneratorPage;
